/**
 * React Query hooks for the scenario library HTTP surface (P12).
 *
 * Mirrors backend/twin/scenario_router.py. The scenario cascade
 * endpoint returns CascadeSummary -- the same shape the live
 * cascade view consumes -- so the rendering primitive is reused.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  CascadeSummary,
  CheapestFixReport,
  ScenarioArea,
  ScenarioDetail,
  ScenarioImpactPatch,
  ScenarioImpactRow,
  ScenarioSummary,
  ScenarioTemplate,
  ToleranceReport,
} from "shared/api/types";

const scenarioKeys = {
  all: ["scenarios"] as const,
  templates: (area?: ScenarioArea) =>
    area
      ? (["scenarios", "templates", area] as const)
      : (["scenarios", "templates"] as const),
  list: () => ["scenarios", "list"] as const,
  detail: (id: string) => ["scenarios", "detail", id] as const,
  cascade: (id: string, as_of?: string) =>
    [...(["scenarios", "cascade", id] as const), as_of ?? "_none"] as const,
};

export function useScenarioTemplates(
  area?: ScenarioArea,
): UseQueryResult<ScenarioTemplate[], ApiError> {
  return useQuery({
    queryKey: scenarioKeys.templates(area),
    queryFn: () => {
      const query = area ? `?area=${encodeURIComponent(area)}` : "";
      return api.get<ScenarioTemplate[]>(`/v1/scenario-templates${query}`);
    },
    staleTime: 5 * 60_000,
  });
}

export function useScenarios(): UseQueryResult<ScenarioSummary[], ApiError> {
  return useQuery({
    queryKey: scenarioKeys.list(),
    queryFn: () => api.get<ScenarioSummary[]>("/v1/scenarios"),
  });
}

export function useScenarioCheapestFix(
  scenarioId: string | null,
  budget?: number | null,
  basis: "hours" | "sar" = "hours",
  live = false,
): UseQueryResult<CheapestFixReport, ApiError> {
  return useQuery({
    queryKey: [
      "scenarios",
      "cheapest-fix",
      scenarioId ?? "",
      budget ?? "_none",
      basis,
      live,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (budget != null && budget > 0) params.set("budget", String(budget));
      if (basis === "sar") params.set("basis", "sar");
      if (live) params.set("live", "true");
      const q = params.toString() ? `?${params.toString()}` : "";
      return api.get<CheapestFixReport>(
        `/v1/scenarios/${encodeURIComponent(scenarioId ?? "")}/cheapest-fix${q}`,
      );
    },
    enabled: !!scenarioId,
    retry: false,
  });
}

export function useScenarioTolerance(
  scenarioId: string | null,
  live = false,
): UseQueryResult<ToleranceReport, ApiError> {
  return useQuery({
    queryKey: ["scenarios", "tolerance", scenarioId ?? "", live],
    queryFn: () =>
      api.get<ToleranceReport>(
        `/v1/scenarios/${encodeURIComponent(scenarioId ?? "")}/tolerance${
          live ? "?live=true" : ""
        }`,
      ),
    enabled: !!scenarioId,
    retry: false,
  });
}

export function useScenarioDetail(
  scenarioId: string | undefined,
): UseQueryResult<ScenarioDetail, ApiError> {
  return useQuery({
    queryKey: scenarioId
      ? scenarioKeys.detail(scenarioId)
      : ["scenarios", "detail", "_disabled"],
    queryFn: () =>
      api.get<ScenarioDetail>(`/v1/scenarios/${encodeURIComponent(scenarioId ?? "")}`),
    enabled: !!scenarioId,
  });
}

export function useScenarioCascade(
  scenarioId: string | undefined,
  opts: { as_of?: string | undefined } = {},
): UseQueryResult<CascadeSummary, ApiError> {
  return useQuery({
    queryKey: scenarioId
      ? scenarioKeys.cascade(scenarioId, opts.as_of)
      : ["scenarios", "cascade", "_disabled"],
    queryFn: () => {
      const q = opts.as_of ? `?as_of=${encodeURIComponent(opts.as_of)}` : "";
      return api.get<CascadeSummary>(
        `/v1/scenarios/${encodeURIComponent(scenarioId ?? "")}/cascade${q}`,
      );
    },
    enabled: !!scenarioId,
  });
}

export function useInstantiateTemplate(): UseMutationResult<
  ScenarioSummary,
  ApiError,
  { templateCode: string; name?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice in two wordings.
    meta: { handled: true },
    mutationFn: ({ templateCode, name }) =>
      api.post<ScenarioSummary>(
        `/v1/admin/scenario-templates/${encodeURIComponent(templateCode)}/instantiate`,
        // Round 14 D9 -- a name of the reader's choosing, when given.
        name?.trim() ? { name: name.trim() } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scenarioKeys.all });
    },
  });
}

export function useDeleteScenario(): UseMutationResult<
  void,
  ApiError,
  { scenarioId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ scenarioId }) =>
      api.delete<void>(`/v1/admin/scenarios/${encodeURIComponent(scenarioId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scenarioKeys.all });
    },
  });
}

/** ROUND 15 D14 -- set or clear what a scenario states about one
 *  target's time and capacity. The tolerance verdicts read the stated
 *  duration as a floor, so the report is refetched with the detail. */
export function usePatchImpact(
  scenarioId: string,
): UseMutationResult<
  ScenarioImpactRow,
  ApiError,
  { impactId: string; body: ScenarioImpactPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    meta: { handled: true },
    mutationFn: ({ impactId, body }) =>
      api.patch<ScenarioImpactRow>(
        `/v1/admin/scenarios/${encodeURIComponent(scenarioId)}/impacts/${encodeURIComponent(impactId)}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scenarioKeys.all });
    },
  });
}

/** The fields of the severe-but-plausible record a PATCH may send. A
 *  key left out is left alone; a key sent as null clears it. */
export interface ScenarioBasisPatch {
  plausibility_basis?: string | null;
  basis_owner_email?: string | null;
  basis_approved_by_email?: string | null;
  basis_next_review?: string | null;
}

/** Round 18 R8 -- cite, or withdraw, the regulator's ask a scenario answers. */
export function useScenarioRegimeRef(scenarioId: string): {
  add: UseMutationResult<ScenarioDetail, ApiError, string>;
  remove: UseMutationResult<ScenarioDetail, ApiError, string>;
} {
  const qc = useQueryClient();
  const base = `/v1/admin/scenarios/${encodeURIComponent(scenarioId)}/regime-refs`;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: scenarioKeys.all });
    // The thread's Expected stage reads the citations.
    void qc.invalidateQueries({ queryKey: ["thread", "scenario", scenarioId] });
  };
  const add = useMutation<ScenarioDetail, ApiError, string>({
    meta: { handled: true },
    mutationFn: (citation: string) => api.post<ScenarioDetail>(base, { citation }),
    onSuccess: refresh,
  });
  const remove = useMutation<ScenarioDetail, ApiError, string>({
    meta: { handled: true },
    mutationFn: (refId: string) =>
      api.delete<ScenarioDetail>(`${base}/${encodeURIComponent(refId)}`),
    onSuccess: refresh,
  });
  return { add, remove };
}

export function usePatchScenarioBasis(
  scenarioId: string,
): UseMutationResult<ScenarioDetail, ApiError, ScenarioBasisPatch> {
  const qc = useQueryClient();
  return useMutation({
    meta: { handled: true },
    mutationFn: (body) =>
      api.patch<ScenarioDetail>(
        `/v1/admin/scenarios/${encodeURIComponent(scenarioId)}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scenarioKeys.all });
      // The scenario's thread reads the basis for its Decided stage.
      void qc.invalidateQueries({ queryKey: ["thread", "scenario", scenarioId] });
    },
  });
}
